import React, {useCallback, useContext, useMemo, useState} from 'react';
import {AgGridReact} from "ag-grid-react";
import {LocalizationAgGrid} from "../../../utils/localization-ag-grid";
import Button from "../../../components/button";
import Modal from "../../../components/modal";
import InputText from "../../../components/input-text";
import {ProjectContext} from "../../../context/project-context"
import InputForm from "../../../components/input-form";
import EquipmentButtons from "./equipment-buttons";
import {FaPlus} from "react-icons/fa";
import RemoveButtonCellRenderer from "../../../components/cell-renderer/remove-button-cell-renderer";
import {toast} from "react-toastify";
import InputDateTime from "../../../components/input-datetime";
import moment from "moment";

const initialData = {
    name : "",
    mark : "",
    serial_number : "",
    guarantee : "",
    responsible : "",
    credential : ""
}
const EquipmentTable = ({equipment, gridRef, resume = false, readOnlyTable, wrapperClass, onClose, submit, onlyClose, autoSubmitOnModalSave = true}) => {
    const [open, setOpen] = useState(false);
    const [modalTitle, setModalTitle] = useState("")
    const [modalWidth, setModalWidth] = useState("")
    const [editingRowIndex, setEditingRowIndex] = useState(null);
    const [modalData, setModalData] = useState(initialData)
    const [newRow, setNewRow] = useState(false)
    const [modalApi, setModalApi] = useState(null)
    const [modalNode, setModalNode] = useState(null)

    const {user} = useContext(ProjectContext);
    const seeCredential = user.permissions.includes("credential");

    const modalRender = (params, title, api, node) => {
        setModalNode(node)
        setModalApi(api)
        setModalData(params.node.data)
        setEditingRowIndex(params.node.id);
        setModalTitle(title)
        setModalWidth("w-[900px] h-[200px]")
        setOpen(true);
    }

    const formatDate = date => {
        if (date) {
            if (typeof date === 'object') {
                return date.toLocaleDateString()
            }

            const dateSplit = date.split("-");
            return `${dateSplit[2]}/${dateSplit[1]}/${dateSplit[0]}`
        }

        return date;
    }

    const [columnDefs] = useState(() => [
        {field: "name", headerName: "Nome"},
        {field: "mark", headerName: "Marca/Modelo"},
        {field: "serial_number", headerName: "Número de série"},
        {field: "guarantee", headerName: "Garantia (vencimento)",
            cellRenderer: params => {
                return formatDate(params.value)
            }},
        {field: "responsible", headerName: "Responsável", flex: 1},
        (!resume && seeCredential) && {
            field: "credential",
            headerName: "Credencial",
            flex: 1,
            minWidth: 300,
            cellEditor: false
        },
        (!resume && seeCredential) && {
            field: "",
            sortable: false,
            width: 100,
            editable: false,
            cellRenderer: EquipmentButtons,
            cellRendererParams: {
                modalRender: modalRender,
                title: "Equipamentos"
            }
        }].filter(Boolean)
    );

    const defaultColDef = useMemo(() => ({resizable: false, editable: false}), []);

    const showColDef = useMemo(() => ({editable: false, suppressMenu: true, cellStyle: { cursor: 'default' }}), []);

    const addContent = useCallback((data) => {
        const newEquipment = {
            name: data.name,
            mark: data.mark,
            model: data.model,
            serial_number: data.serial_number,
            guarantee: data.guarantee,
            part_number: data.part_number,
            responsible: data.responsible,
            observation: data.observation,
            credential: data.credential
        }

        gridRef.current?.api.applyTransaction({add: [newEquipment]});
        setNewRow(false)
        if (autoSubmitOnModalSave) submit();
    }, [autoSubmitOnModalSave, gridRef, submit]);

    const handleInput = (field, value) => {
        setModalData(prev => ({...prev, [field]: value}))
    };

    const onCellClicked = (cell) => {
        if (cell.value) {
            const columnName = cell.colDef.headerName;
            if (columnName === "Credencial" || columnName === "Número de série"){
                navigator.clipboard.writeText(cell.value).then(() => {
                    toast.success(columnName === "Credencial" ?  `${columnName} copiada.` : `${columnName} copiado.`);
                });
            }
        }
    };

    return (
        <div className={"ag-theme-quartz grid mt-4 " + wrapperClass}>
            <AgGridReact
                ref={gridRef}
                rowHeight={32}
                rowData={equipment}
                columnDefs={columnDefs}
                defaultColDef={!resume ? defaultColDef : showColDef}
                animateRows={false}
                suppressRowClickSelection={true}
                gridOptions={{localeText: LocalizationAgGrid}}
                singleClickEdit={true}
                stopEditingWhenCellsLoseFocus={true}
                domLayout='autoHeight'
                context={{readOnlyTable}}
                suppressCellFocus={resume}
                groupHeaderHeight={38}
                onCellClicked={onCellClicked}
            />
            {
                (!resume && seeCredential) &&
                    <div className={"justify-left"}>
                        <Button onClick={() => {
                            setModalTitle("Equipamento")
                            setOpen(true);
                            setNewRow(true)
                            setModalData(initialData)
                        }} target="primary" col={"mt-2"}>
                            <FaPlus/>
                        </Button>
                    </div>
            }
            <Modal isOpen={open} onClose={() => {onlyClose(setOpen, setNewRow)}} title={modalTitle} width={modalWidth}>
                <div className="flex flex-col lg:grid grid-cols-1 gap-x-6 gap-y-4 lg:grid-cols-12 m-5">
                    <InputForm id="name" name="name" value={modalData.name} label="Nome" col="col-span-6"
                               onChange={({ target: { value } }) => handleInput("name", value)} />

                    <InputForm id="mark" name="mark" value={modalData.mark} label="Marca/Modelo" col="col-span-6"
                               onChange={({ target: { value } }) => handleInput("mark", value)}/>

                    <InputForm id="serial_number" name="serial_number" value={modalData.serial_number} label="Número de série" col="col-span-6"
                               onChange={({ target: { value } }) => handleInput("serial_number", value)}/>

                    <InputForm id="responsible" name="responsible" value={modalData.responsible} label="Responsável" col="col-span-6"
                               onChange={({ target: { value } }) => handleInput("responsible", value)}/>

                    <InputDateTime
                        id="guarantee"
                        selected={modalData.guarantee ? moment(modalData.guarantee).toDate() : null}
                        label="Garantia (vencimento)"
                        col="col-span-2"
                        onChange={(date) => handleInput("guarantee", date ? moment(date).format("YYYY-MM-DD") : "")}
                    />

                    {
                        seeCredential &&
                            <InputText id={"credential"} name={"credential"} value={modalData.credential} label={"Credencial"} col="col-span-12"
                                   onChange={({ target: { value } }) => handleInput("credential", value)} disabled={resume}/>
                    }

                </div>
                <div className="flex justify-end gap-2 px-3 py-3 border-t border-gray-300 bg-[#F2F2F2] rounded-b-lg">
                    <div className="flex justify-end gap-3">
                        {
                            !newRow &&
                            <RemoveButtonCellRenderer node={modalNode} api={modalApi} modal={true} setOpen={setOpen}></RemoveButtonCellRenderer>
                        }
                        {
                            (!resume && newRow) ?
                                <Button target="success" onClick={() => {
                                    if(newRow) addContent(modalData)
                                    setOpen(false);
                                }}>
                                    Salvar
                                </Button>
                                :
                                <Button target="success" onClick={() => onClose(setOpen, setNewRow, gridRef, editingRowIndex, modalData, autoSubmitOnModalSave)}>
                                    Salvar
                                </Button>
                        }
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default EquipmentTable;
